import { buildClassification } from './classification';
import { buildClaims } from './claims';
import { countiesMatch } from './countyNormalize';
import { isPrototypeEligible2023 } from './eligibility';
import { buildRankingBenchmark } from './ranking';
import {
  CORE_TOTAL_SECTORS,
  RANKING_UNIVERSE_VERSION_ID,
  assessRankingEligibility,
  qualifiedRankingMembers,
} from './rankingEligibility';
import {
  buildingTransportBalance,
  findDominantSector,
  sumAvailableSectorEmissions,
} from './sectorMapping';
import type {
  BuildGHGInventoryReportInput,
  GeneratedGHGInventoryReport,
  QualityLabel,
  RankingMetric,
  RankingPoolMember,
  ReportBenchmark,
} from './types';
import { HISTORICAL_WARNING, LIVE_MEMBER_LIMITATIONS, LIVE_TECHNICAL_NOTES, PROTOCOL_COPY, TWO_LENS_COPY } from './memberLanguage';
import { reportValue } from './valueHelpers';
import { DEFAULT_PEER_WEIGHTS, PEER_METHOD_VERSION } from './peerSelection';
import { reconcileWasteWastewater } from './wasteWastewater';
import { indexToBaseline, overlappingYears } from './twoLensIndex';

const FROZEN_UNIVERSE = {
  universeVersionId: RANKING_UNIVERSE_VERSION_ID,
  label: 'California municipal CAP roster frozen for 2023 comparisons (482 members; excludes Mountain House)',
  memberCount: 482,
  inventoryYear: 2023,
};

function qualityLabel(reportReady: boolean, coreComplete: boolean, optionalUnavailable: string[]): QualityLabel {
  if (!reportReady) return coreComplete ? 'not_report_ready' : 'partial_inventory';
  if (optionalUnavailable.length > 0) return 'optional_sector_unavailable';
  return 'core_sectors_complete';
}

function rankingUnavailablePlaceholders(year: number): ReportBenchmark[] {
  const specs: Array<{ metric: RankingMetric; label: string; scope: string }> = [
    { metric: 'state_total', label: 'total community emissions (MT CO2e)', scope: 'statewide' },
    { metric: 'state_per_capita', label: 'per-capita emissions (MT CO2e/resident)', scope: 'statewide' },
    { metric: 'county_total', label: 'total community emissions (MT CO2e)', scope: 'county' },
    { metric: 'county_per_capita', label: 'per-capita emissions (MT CO2e/resident)', scope: 'county' },
  ];
  return specs.map((spec) => ({
    comparisonType: spec.metric,
    metricLabel: spec.label,
    scopeLabel: spec.scope,
    rank: null,
    qualifiedCount: 0,
    universeSize: FROZEN_UNIVERSE.memberCount,
    excludedCount: FROZEN_UNIVERSE.memberCount,
    tieMethod: 'n/a',
    publicationStatus: 'blocked',
    commonYear: year,
    eligibility: {
      eligible: false,
      exclusionReasons: ['NO_METHOD_CONSISTENT_COHORT'],
      sectorCoverageUsed: [...CORE_TOTAL_SECTORS],
      rankingMetric: spec.metric,
      inventoryYear: year,
      universeVersionId: FROZEN_UNIVERSE.universeVersionId,
    },
  }));
}

function buildRankings(input: BuildGHGInventoryReportInput, reportReady: boolean): ReportBenchmark[] {
  const live = input.inventorySource === 'live_calculation';
  const unavailable =
    input.rankingAvailability === 'unavailable' || (live && (!input.rankingPool || input.rankingPool.length === 0));
  if (unavailable) return rankingUnavailablePlaceholders(input.inventoryRun.inventoryYear);
  if (!reportReady) return [];

  const year = input.inventoryRun.inventoryYear;
  const jid = input.jurisdiction.jurisdictionId;
  const frozenPool = input.rankingPool.filter((p) => p.inFrozenUniverse);
  const countyMembers = frozenPool.filter((p) => countiesMatch(p.county, input.jurisdiction.county));
  const countyLabel = `${input.jurisdiction.county} County`;

  const specs: Array<{
    metric: RankingMetric;
    label: string;
    scope: string;
    pool: RankingPoolMember[];
    universe: number;
    value: (m: RankingPoolMember) => number;
  }> = [
    {
      metric: 'state_total',
      label: 'total community emissions (MT CO2e)',
      scope: 'statewide',
      pool: frozenPool,
      universe: FROZEN_UNIVERSE.memberCount,
      value: (m) => m.totalMt as number,
    },
    {
      metric: 'state_per_capita',
      label: 'per-capita emissions (MT CO2e/resident)',
      scope: 'statewide',
      pool: frozenPool,
      universe: FROZEN_UNIVERSE.memberCount,
      value: (m) => (m.totalMt as number) / (m.population as number),
    },
    {
      metric: 'county_total',
      label: 'total community emissions (MT CO2e)',
      scope: `within ${countyLabel}`,
      pool: countyMembers,
      universe: countyMembers.length,
      value: (m) => m.totalMt as number,
    },
    {
      metric: 'county_per_capita',
      label: 'per-capita emissions (MT CO2e/resident)',
      scope: `within ${countyLabel}`,
      pool: countyMembers,
      universe: countyMembers.length,
      value: (m) => (m.totalMt as number) / (m.population as number),
    },
  ];

  return specs.map((spec) => {
    const qualified = qualifiedRankingMembers(spec.pool, spec.metric, year);
    const candidates = qualified.map((m) => ({ jurisdictionId: m.jurisdictionId, value: spec.value(m) }));
    const bench = buildRankingBenchmark(spec.metric, spec.label, spec.scope, jid, candidates, spec.universe, year);
    const target = spec.pool.find((m) => m.jurisdictionId === jid);
    const assessment = target
      ? assessRankingEligibility(target, spec.metric, year)
      : {
          eligible: false,
          exclusionReasons: ['MISSING_CANONICAL_GEOGRAPHY' as const],
          sectorCoverageUsed: [...CORE_TOTAL_SECTORS],
        };
    return {
      ...bench,
      rank: assessment.eligible ? bench.rank : null,
      eligibility: {
        eligible: assessment.eligible,
        exclusionReasons: assessment.exclusionReasons,
        sectorCoverageUsed: assessment.sectorCoverageUsed ?? [...CORE_TOTAL_SECTORS],
        rankingMetric: spec.metric,
        inventoryYear: year,
        universeVersionId: FROZEN_UNIVERSE.universeVersionId,
      },
    };
  });
}

export function buildGHGInventoryReport(input: BuildGHGInventoryReportInput): GeneratedGHGInventoryReport {
  const generatedAt = input.generatedAt ?? new Date().toISOString();
  const year = input.inventoryRun.inventoryYear;
  const sectors = input.sectorObservations;
  const sectorSumResult = sumAvailableSectorEmissions(sectors);
  const sectorSum = sectorSumResult.sumMt;
  const live = input.inventorySource === 'live_calculation';
  const inventoryTotalMt = input.inventoryTotalMt ?? input.storedTotalMt ?? null;
  const residual = inventoryTotalMt != null ? inventoryTotalMt - sectorSum : null;

  const pop = input.populationForYear.value;
  const perCapita = pop != null && pop > 0 && inventoryTotalMt != null ? inventoryTotalMt / pop : null;

  const rankingEligibleTotals = qualifiedRankingMembers(input.rankingPool, 'state_total', year);
  const rankingEligiblePc = qualifiedRankingMembers(input.rankingPool, 'state_per_capita', year);

  const dominant = findDominantSector(sectors);
  const bt = buildingTransportBalance(sectors);

  const eligible = isPrototypeEligible2023(input.jurisdiction.stableJurisdictionId);
  const failureReasons: string[] = [];
  if (!live && !eligible) failureReasons.push('City not prototype-eligible for 2023 per foundation audit gates');
  if (inventoryTotalMt == null) failureReasons.push('Missing inventory total for inventory year');
  const coreMissing = CORE_TOTAL_SECTORS.some((code) => {
    const s = sectors.find((x) => x.sectorCode === code);
    if (!s || s.emissionsMt.value == null || s.emissionsMt.status === 'missing') return true;
    if (!live && (code === 'elec_res' || code === 'elec_com')) {
      return s.activity == null || s.activity.value == null || s.activity.status === 'missing';
    }
    return false;
  });
  if (coreMissing) failureReasons.push('One or more core ClimatePlans total sectors missing');

  const reportReady = live
    ? inventoryTotalMt != null
    : eligible && inventoryTotalMt != null && !coreMissing;
  const agriculture = sectors.find((s) => s.sectorCode === 'agriculture');
  const optionalUnavailable: string[] = [];
  if (!agriculture || agriculture.emissionsMt.value == null || agriculture.emissionsMt.status === 'missing') {
    optionalUnavailable.push('Agriculture');
  }
  optionalUnavailable.push('Wastewater (unresolved breakout from the software Waste series)');

  const qLabel = live
    ? reportReady
      ? 'current_calculation_available'
      : 'not_report_ready'
    : qualityLabel(reportReady, !coreMissing, optionalUnavailable.filter((s) => !s.startsWith('Wastewater')));

  const classification = buildClassification({
    reportReady,
    population: pop ?? null,
    density: input.populationDensity?.value ?? null,
    totalMt: inventoryTotalMt,
    perCapitaMt: perCapita,
    dominantSector: dominant,
    buildingTransportComparable: bt.comparable,
    buildingMt: bt.buildingMt,
    transportMt: bt.transportMt,
    poolPopulations: rankingEligiblePc.map((p) => p.population as number),
    poolDensities: input.rankingPool
      .filter((p) => p.inFrozenUniverse && p.density != null && p.density > 0)
      .map((p) => p.density as number),
    poolTotals: rankingEligibleTotals.map((p) => p.totalMt as number),
    poolPerCapita: rankingEligiblePc.map((p) => (p.totalMt as number) / (p.population as number)),
  });

  const histPoints = input.historicalPoints;
  const historicalAllowed =
    (live || reportReady) && histPoints.filter((p) => p.totalEmissionsMt.value != null).length >= 2;

  const draft: GeneratedGHGInventoryReport = {
    identity: {
      jurisdiction: input.jurisdiction,
      universe: input.universe,
      inventoryYear: year,
      generatedAt,
      boundaryLabel: `Community (geographic) inventory of ${input.jurisdiction.canonicalName}. Municipal operations are not inventoried separately.`,
      qualityLabel: qLabel,
      prototypeBanner: PROTOCOL_COPY,
    },
    communityProfile: {
      population: input.populationForYear,
      ...(input.households ? { households: input.households } : {}),
      county: reportValue(input.jurisdiction.county, '', 'observed', { sourceTable: 'population', sourceColumn: 'county', sourceYear: year }),
      ...(input.landAreaSqMi ? { landAreaSqMi: input.landAreaSqMi } : {}),
      ...(input.populationDensity ? { populationDensity: input.populationDensity } : {}),
      ...(input.utilityProvider ? { utilityProvider: input.utilityProvider } : {}),
      ...(input.jobs ? { jobs: input.jobs } : {}),
      ...(input.housingUnits ? { housingUnits: input.housingUnits } : {}),
    },
    sectorSummary: {
      totalEmissionsMt: reportValue(inventoryTotalMt, 'MT CO2e', inventoryTotalMt == null ? 'missing' : 'modeled', {
        sourceTable: live ? 'fetchGHGInventoryData' : 'ghg_inventory_precalculated',
        sourceColumn: live ? 'totalEmissions / chart stack' : 'total_emissions',
        sourceYear: year,
      }),
      perCapitaMt: reportValue(perCapita, 'MT CO2e/resident', perCapita == null ? 'missing' : 'modeled', {
        sourceTable: 'derived',
        calculationRule: 'inventory total / population',
        sourceYear: year,
      }),
      sectorTotals: sectors,
      dominantSector: reportReady ? dominant : null,
    },
    sectorDetail: sectors,
    historical: {
      points: histPoints,
      displayAllowed: historicalAllowed,
      comparabilityLabel: live
        ? historicalAllowed
          ? HISTORICAL_WARNING
          : histPoints.length === 0
            ? 'A multi-year series is not available from the current calculation.'
            : 'A trend is not shown because fewer than two years are available.'
        : !reportReady
          ? 'Historical trend suppressed — report not ready'
          : historicalAllowed
            ? 'Descriptive multi-year series — method comparability not verified'
            : histPoints.length === 0
              ? 'No historical inventory rows available'
              : 'Insufficient history for trend display (requires at least two years)',
      methodBreakNotes: ['Production database does not store inventory method version per year'],
    },
    consumption: input.consumption,
    twoLens: {
      sectorTotalMt: reportValue(inventoryTotalMt, 'MT CO2e', inventoryTotalMt == null ? 'missing' : 'modeled', {
        sourceTable: live ? 'fetchGHGInventoryData' : 'ghg_inventory_precalculated',
        sourceColumn: live ? 'totalEmissions / chart stack' : 'total_emissions',
      }),
      consumptionTotalMt: input.consumption?.totalMt ?? reportValue<number | null>(null, 'MT CO2e', 'missing', { sourceTable: 'cbei_cities' }),
      ratioSuppressed: true,
      scopeExplanation: TWO_LENS_COPY,
    },
    rankings: buildRankings(input, reportReady),
    classification,
    claims: [],
    quality: {
      overallLabel: qLabel,
      prototypeStatus: 'internal_prototype',
      missingSectors: sectors.filter((s) => s.emissionsMt.status === 'missing').map((s) => s.sectorLabel),
      optionalSectorsUnavailable: optionalUnavailable,
      coreSectorsComplete: live ? false : !coreMissing,
      presentationStatus: reportReady ? 'current_calculation_available' : 'calculation_unavailable',
      activityCoverageVerified: false,
      zeroMayRepresentFilledMissing: live,
      sectorSumIncluded: sectorSumResult.includedSectors,
      sectorSumExcluded: sectorSumResult.excludedSectors,
      sectorSumCompleteness: sectorSumResult.completenessLabel,
      sectorSumResidualMt: residual,
      storedTotalMt: inventoryTotalMt,
      trendComparable: false,
      rankingPublicationReady: false,
    },
    methodology: {
      limitations: live ? [...LIVE_MEMBER_LIMITATIONS] : [
        'Legacy ghg_inventory_precalculated rows are mutable snapshots, not preserved run history',
        'CBEI model name/version not stored in database; household count is a CBEI model field, not a Census observation',
        'Wastewater is not returned as a separate series by fetchGHGInventoryData',
        'Agriculture is optional for municipal inventories and does not imply a complete multi-sector protocol inventory',
        'Historical trends are descriptive only',
        'CCA/LSE identity from ccas is not year-stamped; shown as applicable for the inventory year',
        'Accepted production/C2R3 values only — experimental P5 Rev2 not substituted',
        'Class C diagnostic references (e.g. Burbank nonresidential gas, Long Beach gas) excluded from calibration and rankings',
      ],
      missingSectors: optionalUnavailable,
      technicalNotes: live ? [...LIVE_TECHNICAL_NOTES] : undefined,
    },
    readiness: {
      reportReady,
      failureReasons,
    },
    inventoryRun: input.inventoryRun,
    activityEvidence: input.activityEvidence ?? null,
    wasteWastewater: reconcileWasteWastewater({
      combinedWasteMt: sectors.find((s) => s.sectorCode === 'waste')?.emissionsMt.value ?? null,
      solidWasteMt: (() => {
        const combined = sectors.find((s) => s.sectorCode === 'waste')?.emissionsMt.value ?? null;
        const ww = sectors.find((s) => s.sectorCode === 'water_wastewater')?.emissionsMt.value ?? null;
        if (combined != null && ww != null) return combined - ww;
        return combined;
      })(),
      wastewaterMt: sectors.find((s) => s.sectorCode === 'water_wastewater')?.emissionsMt.value ?? null,
    }),
    consumptionHistory: input.consumptionHistory ?? [],
    jobs: input.jobs,
    housingUnits: input.housingUnits,
    comparisonSnapshot: {
      available: false,
      omissionNote:
        'Numeric statewide, county, and peer comparisons are omitted until a method-consistent inventory snapshot exists. This report does not calculate other jurisdictions when it loads.',
    },
    peerSelection: {
      methodVersion: PEER_METHOD_VERSION,
      eligibleCount: 0,
      selected: [],
      suppressed: true,
      suppressionReason:
        'Peer cities are not published in this report because a method-consistent eligible pool of at least eight incorporated cities is not available without calculating other jurisdictions on load.',
      weights: DEFAULT_PEER_WEIGHTS,
    },
    scenarioHandoff: input.scenarioHandoff,
    replicaDisclosure: input.replicaDisclosure,
    twoLensIndex: (() => {
      const sectorPts = histPoints.map((p) => ({ year: p.year, value: p.totalEmissionsMt.value }));
      const cbeiPts = (input.consumptionHistory ?? []).map((p) => ({ year: p.year, value: p.totalMt }));
      const years = overlappingYears(sectorPts, cbeiPts);
      const modelVersionMissing = true;
      if (modelVersionMissing || years.length < 2) {
        return {
          baselineYear: year,
          overlappingYears: years,
          sectorIndex: [],
          consumptionIndex: [],
          suppressed: true,
          suppressionReason:
            'Indexed two-lens comparison is suppressed because the consumption model version is not stored with the report data, so continuity is not defensible.',
        };
      }
      const sectorIdx = indexToBaseline(sectorPts.filter((p) => years.includes(p.year)), years[0]);
      const cbeiIdx = indexToBaseline(cbeiPts.filter((p) => years.includes(p.year)), years[0]);
      return {
        baselineYear: years[0],
        overlappingYears: years,
        sectorIndex: sectorIdx.points,
        consumptionIndex: cbeiIdx.points,
        suppressed: sectorIdx.suppressed || cbeiIdx.suppressed,
        suppressionReason: sectorIdx.reason || cbeiIdx.reason,
      };
    })(),
    tractConsumption: input.tractConsumption ?? {
      omitted: true,
      omissionNote: 'Tract-level consumption geometry is not wired for a print-safe city choropleth in this version.',
    },
    transportDiagnostics: input.transportDiagnostics,
  };

  draft.claims = buildClaims(draft);
  return draft;
}

export { FROZEN_UNIVERSE };
